import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { useT } from '../i18n';
import { Icon } from './Icon';

/**
 * How many dialogs are open right now.
 *
 * A picture opened from inside a story, a relative's card opened from inside a
 * profile — dialogs stack, and the browser's top layer handles that for us. The
 * background scroll lock does not: whoever closes first used to hand the page
 * back its scrollbar while another dialog was still open. So the lock is
 * counted, and only the last one out unlocks.
 */
let openDialogs = 0;
function lockBackground() {
  openDialogs += 1;
  document.body.style.overflow = 'hidden';
}
function unlockBackground() {
  openDialogs = Math.max(0, openDialogs - 1);
  if (openDialogs === 0) document.body.style.overflow = '';
}

/** How far a sheet has to be dragged down before letting go closes it. */
const SWIPE_CLOSE_PX = 96;

/**
 * Accessible modal: focus trap, Escape to close, backdrop click, returns focus.
 * Bottom sheet on mobile, centred panel on desktop — and on a touch screen the
 * sheet can be pushed down off the bottom with a finger, the way every other
 * app on the phone behaves.
 */
export function Dialog({
  open,
  onClose,
  title,
  children,
  actions,
  wide,
  labelledBy,
  closeLabel,
  variant = 'sheet',
  panelClassName = '',
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  actions?: ReactNode;
  wide?: boolean;
  labelledBy?: string;
  closeLabel?: string;
  /**
   * `sheet`: bottom sheet / centred box. `panel`: a tall panel sliding in from
   * the side on wide screens, over the current screen. `bare`: the top layer
   * and the backdrop only — the child brings its own frame (the person card).
   */
  variant?: 'sheet' | 'panel' | 'bare';
  /** Extra class on the panel itself — for a bare dialog whose child brings a frame of its own. */
  panelClassName?: string;
}) {
  const t = useT();
  const ref = useRef<HTMLDialogElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const restoreTo = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const dlg = ref.current;
    if (!dlg) return;
    if (open && !dlg.open) {
      restoreTo.current = document.activeElement as HTMLElement | null;
      try {
        dlg.showModal();
      } catch {
        dlg.setAttribute('open', '');
      }
      lockBackground();
      requestAnimationFrame(() => {
        const first = panelRef.current?.querySelector<HTMLElement>(
          'input:not([type=hidden]), select, textarea, button:not([data-autofocus-skip]), [href], [tabindex]:not([tabindex="-1"])',
        );
        (first ?? panelRef.current)?.focus?.();
      });
      return () => {
        // The element goes away with `open` (see the early return below), so the
        // count is settled here rather than in a bare cleanup.
        unlockBackground();
      };
    }
    if (!open && dlg.open) {
      dlg.close();
      restoreTo.current?.focus?.();
    }
    return undefined;
  }, [open]);

  useEffect(() => {
    const dlg = ref.current;
    if (!dlg) return;
    const onCancel = (e: Event) => {
      e.preventDefault();
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !panelRef.current) return;
      const items = [...panelRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')].filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    // Newer browsers may close a modal on Escape without letting `cancel` stop it (close watchers);
    // treat a native close as the user closing, so React's state follows the dialog's.
    const onNativeClose = () => onClose();
    dlg.addEventListener('cancel', onCancel);
    dlg.addEventListener('close', onNativeClose);
    dlg.addEventListener('keydown', onKey);
    return () => {
      dlg.removeEventListener('cancel', onCancel);
      dlg.removeEventListener('close', onNativeClose);
      dlg.removeEventListener('keydown', onKey);
    };
    // `open` is a dependency because the <dialog> only exists while open: the listeners must be
    // attached to each new element (a stable onClose alone would attach them once, to nothing).
  }, [onClose, open]);

  /*
   * Push the sheet down to put it away.
   *
   * Only a finger or a pen does this, only while the sheet is scrolled to the
   * top, and only once the gesture is clearly a downward drag rather than a
   * scroll — so reading a long story is never mistaken for closing it. Below
   * the threshold the sheet springs back.
   */
  const drag = useRef<{ id: number; y: number; x: number; on: boolean } | null>(null);
  const [dragging, setDragging] = useState(false);

  /*
   * The sheet is moved by writing the transform straight onto the element. A
   * finger fires a move event every frame and this panel can hold a whole
   * story, so re-rendering that on each one is exactly the jank a phone cannot
   * afford; React only hears about the drag starting and stopping.
   */
  const setOffset = (px: number) => {
    const panel = panelRef.current;
    if (panel) panel.style.transform = px > 0 ? `translateY(${px}px)` : '';
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (variant === 'bare' || e.pointerType === 'mouse') return;
    const panel = panelRef.current;
    if (!panel || panel.scrollTop > 0) return;
    drag.current = { id: e.pointerId, y: e.clientY, x: e.clientX, on: false };
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    const dy = e.clientY - d.y;
    const dx = e.clientX - d.x;
    if (!d.on) {
      // Not a drag until it has moved a little, downwards, and more down than sideways.
      if (dy < 10 || Math.abs(dx) > Math.abs(dy)) {
        if (dy < -4 || Math.abs(dx) > 10) drag.current = null;
        return;
      }
      d.on = true;
      setDragging(true);
    }
    setOffset(Math.max(0, dy));
  };
  const endDrag = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const d = drag.current;
      if (!d || d.id !== e.pointerId) return;
      const dy = Math.max(0, e.clientY - d.y);
      drag.current = null;
      setDragging(false);
      setOffset(0);
      if (d.on && dy > SWIPE_CLOSE_PX) onClose();
    },
    [onClose],
  );

  if (!open) return null;

  const bare = variant === 'bare';
  return (
    <dialog
      className={`dlg ${variant === 'panel' ? 'dlg-side' : ''} ${bare ? 'dlg-bare' : ''}`}
      ref={ref}
      aria-labelledby={labelledBy ?? 'dlg-title'}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      <div
        className={`dlg-panel ${wide ? 'wide' : ''} ${variant === 'panel' ? 'is-side' : ''} ${bare ? 'is-bare' : ''} ${dragging ? 'is-dragging' : ''} ${panelClassName}`}
        ref={panelRef}
        tabIndex={-1}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        {bare ? (
          <>
            <h2 id={labelledBy ?? 'dlg-title'} className="sr-only">{title}</h2>
            {children}
          </>
        ) : (
          <>
            <div className="dlg-grip" />
            <div className="dlg-head">
              <h2 id={labelledBy ?? 'dlg-title'}>{title}</h2>
              {/*
                * A panel takes the whole screen on a phone, so for a moment there
                * is nothing else — the way out says hello twice and then keeps
                * quiet (see .back-announce). A sheet slides up over the page you
                * can still see, so it needs no introduction.
                */}
              <button type="button" className={`btn btn-ghost btn-icon is-back ${variant === 'panel' ? 'back-announce' : ''}`} onClick={onClose} aria-label={closeLabel ?? t('nav.close')}>
                <Icon name="close" />
              </button>
            </div>
            {children}
            {actions ? <div className="dlg-actions">{actions}</div> : null}
          </>
        )}
      </div>
    </dialog>
  );
}

/** Simple confirmation with an explicit description of what will happen. */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  body,
  confirmLabel,
  danger,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  body: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
}) {
  const t = useT();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      actions={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {t('nav.cancel')}
          </button>
          <button
            type="button"
            className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`}
            onClick={() => {
              onConfirm();
              onClose();
            }}
          >
            {confirmLabel ?? t('common.confirm')}
          </button>
        </>
      }
    >
      <div className="muted">{body}</div>
    </Dialog>
  );
}

/**
 * Confirm something *and* offer to say why.
 *
 * Used before every administrator action the family will notice. The reason is
 * optional on purpose: a required box only teaches people to type a full stop,
 * and no reason at all is more honest than a fake one. Whatever is typed is
 * kept with the decision and shown in Admin controls → Admin decisions.
 */
export function ReasonDialog({
  open,
  title,
  body,
  confirmLabel,
  onClose,
  onConfirm,
  danger,
}: {
  open: boolean;
  title: string;
  body: ReactNode;
  confirmLabel: string;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  danger?: boolean;
}) {
  const t = useT();
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (open) setReason('');
  }, [open]);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      actions={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {t('nav.cancel')}
          </button>
          <button
            type="button"
            className={`btn ${danger ? 'btn-danger' : 'btn-primary is-admin-control'}`}
            onClick={() => {
              onConfirm(reason.trim());
              onClose();
            }}
          >
            {confirmLabel}
          </button>
        </>
      }
    >
      <div className="stack">
        <div className="muted">{body}</div>
        <div className="field">
          <label className="field-label" htmlFor="reason-text">{t('decisions.reasonLabel')}</label>
          <textarea id="reason-text" className="input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('decisions.reasonPlaceholder')} />
          <p className="help" style={{ marginBottom: 0 }}>{t('decisions.reasonHelp')}</p>
        </div>
      </div>
    </Dialog>
  );
}
